import { Controller } from "@hotwired/stimulus";
import { select2Matcher } from "../utils/text_match";

// Single controller scoped to the cockpit root that drives every secondary
// modal (edit etude, edit team, etc.). Each modal is a sibling element with
// its own DOM id; triggers reference it with `data-modal-id`. We deliberately
// avoid a per-modal controller so everything that opens/closes goes through
// the same code path — easier to keep ESC, backdrop click and focus trap
// consistent.
export default class extends Controller {
  static targets = ["modal"];

  connect() {
    this._onKey = this._onKey.bind(this);
    document.addEventListener("keydown", this._onKey);
    // Snapshot cleanup. Turbo caches the DOM verbatim — including any
    // Select2-injected `<span class="select2-container">` siblings AND the
    // hidden-accessible class on every <select>. On back-restore, our open()
    // would happily call select2() again on top, doubling every dropdown.
    // We pre-empt the cache by destroying every Select2 instance and closing
    // any open modal at snapshot time so the restored HTML is clean.
    this._beforeCache = () => this._beforeTurboCache();
    document.addEventListener("turbo:before-cache", this._beforeCache);
    // Deep-link : `?modal=<id>` ouvre le modal correspondant au chargement
    // (ex. quick action du HUD "Nouveau contact" → index contacts + wizard).
    const wanted = new URLSearchParams(window.location.search).get("modal");
    if (wanted) {
      const modal = this._findModal(wanted);
      if (modal) this._openModal(modal);
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey);
    if (this._beforeCache) document.removeEventListener("turbo:before-cache", this._beforeCache);
  }

  _beforeTurboCache() {
    const $ = window.jQuery || window.$;
    this.modalTargets.forEach((modal) => {
      // Force-close before snapshot
      modal.classList.remove("esm_modal--open");
      modal.setAttribute("hidden", "");
      // Tear down Select2 so the cached snapshot doesn't carry orphan
      // containers and the `select2-hidden-accessible` class.
      if ($ && $.fn.select2) {
        $(modal).find(".searchable-select").each(function () {
          const $sel = $(this);
          if ($sel.hasClass("select2-hidden-accessible")) {
            try { $sel.select2("destroy"); } catch (_e) { /* noop */ }
          }
        });
      }
    });
    document.body.classList.remove("esm_modal-lock");
  }

  open(event) {
    event.preventDefault();
    const id = event.currentTarget.dataset.modalId;
    if (!id) return;
    const modal = this._findModal(id);
    if (!modal) return;
    this._openModal(modal);
  }

  _openModal(modal) {
    modal.classList.add("esm_modal--open");
    modal.removeAttribute("hidden");
    document.body.classList.add("esm_modal-lock");
    // Notify the modal content (e.g. document-wizard) that it just became
    // visible, so it can refresh server-dependent data (signataires…).
    modal.dispatchEvent(new CustomEvent("cockpit-modal:opened", { bubbles: true }));
    // Initialize Select2 for searchable selects now that the modal is visible.
    // Doing it before the modal had dimensions caused weird widths.
    this._initSearchableSelects(modal);
    setTimeout(() => {
      const first = modal.querySelector("input:not([type='hidden']), select:not(.searchable-select), textarea, button[data-role='primary']");
      first?.focus();
    }, 50);
  }

  close(event) {
    if (event) event.preventDefault();
    const modal = event ? this._modalFromEvent(event) : null;
    if (modal) {
      this._closeModal(modal);
    } else {
      // Close every open modal if no specific target.
      this.modalTargets.forEach((m) => this._closeModal(m));
    }
  }

  // Allow clicking the dimmed overlay to close — but ignore clicks on the panel.
  closeFromBackdrop(event) {
    if (event.target === event.currentTarget) {
      this._closeModal(event.currentTarget.closest(".esm_modal"));
    }
  }

  _onKey(event) {
    if (event.key !== "Escape") return;
    const open = this.modalTargets.find((m) => m.classList.contains("esm_modal--open"));
    if (open) this._closeModal(open);
  }

  _modalFromEvent(event) {
    return event.currentTarget.closest(".esm_modal");
  }

  _findModal(id) {
    return this.modalTargets.find((m) => m.id === id);
  }

  _closeModal(modal) {
    if (!modal) return;
    modal.classList.remove("esm_modal--open");
    // Wait for the fade-out animation before hiding to avoid layout flash.
    setTimeout(() => {
      if (!modal.classList.contains("esm_modal--open")) {
        modal.setAttribute("hidden", "");
      }
    }, 180);
    if (!this.modalTargets.some((m) => m.classList.contains("esm_modal--open"))) {
      document.body.classList.remove("esm_modal-lock");
    }
  }

  // Promote any `.searchable-select` inside the just-opened modal into Select2.
  // We re-init each time so that selects added via Turbo or with newly visible
  // dimensions get a correctly-sized dropdown. The dropdown is attached to the
  // panel itself so it inherits the modal's stacking context.
  _initSearchableSelects(modal) {
    const $ = window.jQuery || window.$;
    if (!$ || !$.fn.select2) return;
    // Attach the dropdown to the .esm_modal container (full-viewport, no
    // overflow clipping) so the menu can render below or above any field
    // without being cropped by the panel's rounded edges or scrollable body.
    setTimeout(() => {
      $(modal).find(".searchable-select").each(function () {
        const $sel = $(this);
        if ($sel.hasClass("select2-hidden-accessible")) {
          $sel.select2("destroy");
        }
        $sel.select2({
          placeholder: $sel.data("placeholder") || "Rechercher…",
          allowClear: $sel.data("allow-clear") !== false,
          width: "100%",
          dropdownParent: $(modal),
          language: {
            noResults: () => $sel.data("no-results") || "Aucun résultat",
            searching: () => "Recherche…",
          },
          // Recherche par mots, insensible à la casse, aux accents et à
          // l'ordre, et qui descend dans les <optgroup> (picker de contacts
          // groupé par entreprise). Logique partagée avec le reste de l'app.
          matcher: select2Matcher,
        });
      });
    }, 30);
  }
}
